import { createSelector, MemoizedSelector } from '@ngrx/store';

import { memoizeSelector, namedProjector } from '../selector-utils';
import { BaseMetaState, MetaState, Reference } from '../types';
import { createSelectorWithReferences } from '../utils';
import {
  BaseEntity,
  BaseSelector,
  EntityState,
  ID,
  InferScopeMeta,
  MetaOptions,
  MetaScopeOptions,
  Scope,
} from './types';
import { resolveScope } from './utils';

type MetaSelectorProps = ID | { scope: Scope };

// eslint-disable-next-line max-lines-per-function
export const getSelectors = <T extends BaseEntity, U extends MetaOptions>(
  baseSelector: BaseSelector<T, U>,
  namespace: string,
  references: Reference<T>[] | null,
) => {
  const entityMap = createSelector(baseSelector, state => state.entityMap);
  const entityMetaMap = createSelector(baseSelector, state => state.entityMetaMap);
  const aliasesMap = createSelector(baseSelector, state => state.aliases);
  const scopeMapSelector = createSelector(baseSelector, state => state.scope);
  const scopePaginationMapSelector = createSelector(baseSelector, state => state.scopePagination);
  const scopeMetaMapSelector = createSelector(baseSelector, state => state.scopeMeta);
  const scopeMetaSelector = memoizeSelector((scope?: string | null) =>
    createSelector(
      scopeMetaMapSelector,
      namedProjector(
        `${namespace}ScopeMetaMapSelector`,
        scopeMetaMap => scopeMetaMap[resolveScope(scope)],
      ),
    ),
  );
  const scopePaginationSelector = memoizeSelector((props?: MetaScopeOptions) => {
    const resolvedScope = resolveScope(props?.scope);
    return createSelector(
      scopePaginationMapSelector,
      namedProjector(
        `${namespace}ScopePaginationSelector`,
        scopePaginationMap => scopePaginationMap[resolvedScope],
      ),
    );
  });
  const idByAliasSelector = memoizeSelector((alias: string) =>
    createSelector(
      aliasesMap,
      namedProjector(`${namespace}IdByAliasSelector`, aliasesMap => aliasesMap[alias]),
    ),
  );

  const entityMeta = memoizeSelector((id: ID) =>
    createSelector(
      entityMetaMap,
      namedProjector(`${namespace}EntityMetaSelector`, entityMetaMap => entityMetaMap[id]),
    ),
  );

  const scopeSelector = memoizeSelector((scope?: string | null) =>
    createSelector(
      scopeMapSelector,
      namedProjector(`${namespace}ScopeSelector`, scopeMap => {
        const resolvedScope = resolveScope(scope);
        return scopeMap[resolvedScope];
      }),
    ),
  );

  const byId = memoizeSelector((id: string | number) => {
    const idFromAlias = idByAliasSelector(`${id}`);
    return createSelector(
      entityMap,
      idFromAlias,
      namedProjector(
        `${namespace}ById`,
        (entities: EntityState<T>['entityMap'], alias: EntityState<T>['aliases'][number]) => {
          const entityId = alias || id;
          return entities[entityId] || null;
        },
      ),
    );
  });

  const getMetaSelector = <K extends keyof MetaState>(metaProp: K) =>
    memoizeSelector((scopeOrEntityId?: MetaSelectorProps) => {
      if (!scopeOrEntityId || (<{ scope: Scope }>scopeOrEntityId)?.scope) {
        const resolvedScope = resolveScope((<{ scope: Scope }>scopeOrEntityId)?.scope);
        return <MemoizedSelector<any, MetaState[K]>>createSelector(
          scopeMetaSelector(resolvedScope),
          namedProjector(`${namespace}-scope-${metaProp}`, scopeMeta => scopeMeta?.[metaProp]),
        );
      } else {
        const idOrAlias = <ID>scopeOrEntityId;
        const idFromAlias = idByAliasSelector(`${idOrAlias}`);
        return <MemoizedSelector<any, MetaState[K]>>createSelector(
          entityMeta(idOrAlias),
          state => entityMeta(idFromAlias(state))(state),
          namedProjector(
            `${namespace}-ID-${metaProp}`,
            (metaFromId, metaFromAlias) => (metaFromId || metaFromAlias)?.[metaProp],
          ),
        );
      }
    });

  const isLoaded = getMetaSelector('loaded');
  const isPending = getMetaSelector('pending');
  const isFailed = getMetaSelector('failed');
  const error = getMetaSelector('error');
  const scopeCustomMeta = memoizeSelector((options?: { scope: Scope }) =>
    createSelector(
      baseSelector,
      namedProjector(`${namespace}custom`, (state: EntityState<T, U>) => {
        const scope = resolveScope(options?.scope);
        const scopeMeta = <BaseMetaState & { custom: InferScopeMeta<U> }>state.scopeMeta[scope];
        return scopeMeta?.custom;
      }),
    ),
  );

  const meta = memoizeSelector((idOrScope: MetaSelectorProps) =>
    createSelector(
      isLoaded(idOrScope),
      isPending(idOrScope),
      isFailed(idOrScope),
      error(idOrScope),
      namedProjector(
        `${namespace}Meta`,
        (isLoaded: boolean, isPending: boolean, isFailed: boolean, error: any) => ({
          isLoaded,
          isPending,
          isFailed,
          error,
        }),
      ),
    ),
  );

  const entities = memoizeSelector((props?: MetaScopeOptions) =>
    createSelector(
      scopeSelector(props?.scope),
      entityMap,
      namedProjector(
        `${namespace}Entities`,
        (scope: EntityState<T>['scope'][0], entityMap: EntityState<T>['entityMap']) =>
          scope?.map(id => entityMap[id]!) || [],
      ),
    ),
  );

  return {
    isLoaded,
    isPending,
    isFailed,
    error,
    meta,
    pagination: scopePaginationSelector,
    byId,
    scopeCustomMeta,
    byIdWithReferences: memoizeSelector((id: number) =>
      createSelectorWithReferences(
        byId(id),
        references,
        namedProjector(`${namespace}ByIdWithReferences`, state => state),
      ),
    ),
    entities,
    allEntities: createSelector(
      entityMap,
      entityMap => <T[]>Object.values(entityMap).filter(Boolean),
    ),
    entitiesWithPagination: memoizeSelector((props?: MetaScopeOptions) =>
      createSelector(scopePaginationSelector(props), entities(props), (pagination, entities) => ({
        meta: pagination,
        rows: entities,
      })),
    ),
    entitiesWithReferences: memoizeSelector((props?: MetaScopeOptions) =>
      createSelectorWithReferences(
        entities(props),
        references,
        namedProjector(`${namespace}EntitiesWithReferences`, entities => {
          if (!Array.isArray(entities)) {
            debugger;
          }

          return entities;
        }),
      ),
    ),
  };
};
